import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Table,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AppointmentsList from './AppointmentsList';
import ConsultationView from './ConsultationView';
import { appointmentApi } from '../api/appointmentApi';
import { consultationApi } from '../api/consultationApi';
import type { ConsultationResponseDto } from '../types/consultation';

interface PastAppointmentsDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  currentAppointmentId?: string;
  onCopy?: (consultation: ConsultationResponseDto) => void;
}

const PastAppointmentsDialog: React.FC<PastAppointmentsDialogProps> = ({ open, onClose, patientId, currentAppointmentId, onCopy }) => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<any | null>(null);
  const [consultationLoading, setConsultationLoading] = useState(false);

  useEffect(() => {
    if (open && patientId && currentAppointmentId) {
      setLoading(true);
      appointmentApi.getById(currentAppointmentId)
        .then((currentAppt: any) => {
          const currentStartTime = new Date(currentAppt.startTime);
          appointmentApi.getAll({ patientId, limit: 100 })
            .then((res: any) => {
              const data = res.data || [];
              // Show all appointments for the patient except the current one
              setAppointments(data.filter((appt: any) => appt.id !== currentAppointmentId));
            })
            .finally(() => setLoading(false));
        })
        .catch(() => setLoading(false));
    }
  }, [open, patientId, currentAppointmentId]);


// ...
const handleViewConsultation = async (appointmentId: string) => {
    setConsultationLoading(true);
    setSelectedConsultation(null);
    try {
      const consultation = await consultationApi.getByAppointmentId(appointmentId);
      setSelectedConsultation(consultation);
    } finally {
      setConsultationLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Past Appointments</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : appointments.length === 0 ? (
          <Typography>No past appointments found.</Typography>
        ) : (
          <Box>
            {console.log('Appointments being rendered in PastAppointmentsDialog:', appointments)}
            <Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appt: any) => (
                    <TableRow key={appt.id}>
                      <TableCell>{appt.date || (appt.startTime && new Date(appt.startTime).toLocaleDateString())}</TableCell>
                      <TableCell>{appt.time || (appt.startTime && new Date(appt.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}</TableCell>
                      <TableCell>{appt.doctorName || appt.doctor?.fullName || appt.doctor?.designation || '-'}</TableCell>
                      <TableCell>{appt.status}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Consultation">
                          <IconButton size="small" onClick={() => handleViewConsultation(appt.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {consultationLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                <CircularProgress size={28} />
              </Box>
            )}
            {selectedConsultation && (
              <Box mt={3}>
                <ConsultationView consultation={selectedConsultation} />
                {onCopy && (
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => onCopy(selectedConsultation)}
                    >
                      Copy to Current Consultation
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PastAppointmentsDialog;
